import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import { kebabCase } from 'lodash'

import { H5, H6 } from 'lib/ui/primitives'

import SubSection from 'components/page/Section/SubSection'
import TestimonialCarousel from 'components/TestimonialCarousel'
import UniversalLink from 'components/UniversalLink'
import FeatureSummary, {
  Container,
} from 'components/summaryCards/FeatureSummary'
import FAQs, { Item } from 'components/FAQs'

import IconBook from '@anvilco-pkg/ui/components/Icon/IconBook'
import IconEasy from '@anvilco-pkg/ui/components/Icon/IconEasy'
import IconQuestion from '@anvilco-pkg/ui/components/Icon/IconQuestion'
import IconBlog from '@anvilco-pkg/ui/components/Icon/IconBlog'
import Carousel from '@anvilco-pkg/ui/components/Carousel'
import { PRODUCTS } from '@anvilco-pkg/sanity'

import { FEATURE_SLUG } from 'consts'

import theme from 'theme'
import media, { pixels } from 'media'
import { getProductSlug } from 'helpers/path'

import RelatedResource from './RelatedResource'

const SubCategory = styled(Flex).attrs({
  flexDirection: ['column', 'column', 'column', 'row'],
  pb: [32, 32, 32, 64],
  mb: [32, 32, 32, 64],
  alignItems: 'flex-start',
})`
  gap: 32px;
  border-bottom: 2px solid ${theme.colors.blacks[10]};

  &:last-of-type {
    border-bottom-width: 0px;
  }
`

const SubCategoryTitle = styled(Flex).attrs({ alignItems: 'baseline' })`
  flex: 1;
  gap: 12px;

  svg {
    flex-shrink: 0;
  }
`

const SubTitle = styled(H5).attrs({ as: 'h2' })``

const SubCategoryContent = styled(Box).attrs({
  width: ['100%', '100%', '100%', 640],
})``

const StyledFAQs = styled(FAQs)`
  margin-top: -20px;

  ${Item} {
    &:last-of-type {
      border-bottom: none;
    }
  }
`

const UseCasesContainer = styled(Flex).attrs({
  flexDirection: ['column', 'column', 'row'],
})`
  gap: 20px;
`

const UseCase = styled(Box).attrs({ flex: 1 })``

const UseCaseTitle = styled(H6).attrs({
  as: 'h3',
  fontSize: [18, 24],
  mb: 10,
})``

const UseCaseDescription = styled(Box).attrs({ fontSize: 15 })``

const BenefitsContainer = styled(Box)`
  display: grid;
  gap: 16px;

  ${media.tablet`
    gap: 32px;
  `}
`

const Benefit = styled(Box)``

const BenefitTitle = styled(H6).attrs({
  as: 'h3',
  fontSize: [18],
  mb: 8,
})``

const BenefitDescription = styled(Box).attrs({ fontSize: 16 })``

const ResourcesContainer = styled(Box)`
  display: grid;
  gap: 32px;
`

const PDFFeaturesContainer = styled(Box).attrs({ pt: [40, 40, 40, 75] })``

const PDFFeaturesSubTitle = styled(H5).attrs({ as: 'h2', mb: 24 })``

const StyledUniversalLink = styled(UniversalLink)`
  font-size: 16px;
`

const StyledCarousel = styled(Carousel).withConfig({
  shouldForwardProp: (prop) => ['featuresCount'].indexOf(prop) === -1,
})`
  .slick-track {
    display: flex !important;
  }

  .slick-slide {
    height: inherit !important;
    display: flex !important;
    justify-content: center;

    & > div {
      height: inherit !important;
      display: flex !important;
    }

    ${Container} {
      height: inherit;
    }
  }

  .slick-next,
  .slick-prev {
    display: none !important;
  }
`

const FeatureContainer = styled(Box).attrs({
  px: [10],
})`
  display: inline-flex !important;
  flex-direction: column;
  height: inherit;
  max-width: 100%;

  ${Container} {
    flex: 1;
  }
`

const CarouselWrapper = styled(Box)
  .attrs({ width: '100%' })
  .withConfig({
    shouldForwardProp: (prop) => ['featuresCount'].indexOf(prop) === -1,
  })`
    display: block;

    ${media.desktop`
      display: ${({ featuresCount }) => (featuresCount > 2 ? 'block' : 'none')};
    `}

    ${media.giant`
      display: ${({ featuresCount }) => (featuresCount > 3 ? 'block' : 'none')};
    `}

    @media (min-width: ${theme.paperMaxWidth}px) {
      display: ${({ featuresCount }) => (featuresCount > 4 ? 'block' : 'none')};
    }
  `

const FeaturesGrid = styled(Box).withConfig({
  shouldForwardProp: (prop) => ['featuresCount'].indexOf(prop) === -1,
})`
  display: none;

  ${media.desktop`
    display: ${({ featuresCount }) => (featuresCount > 2 ? 'none' : 'grid')};
    grid-template-columns: 1fr 1fr;
  `}

  ${media.giant`
    display: ${({ featuresCount }) => (featuresCount > 3 ? 'none' : 'grid')};
    grid-template-columns: 1fr 1fr 1fr;
  `}

  @media (min-width: ${theme.paperMaxWidth}px) {
    display: ${({ featuresCount }) => (featuresCount > 4 ? 'none' : 'grid')};
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }

  ${Container} {
    height: inherit;
  }
`

const FeatureContentSubSection = ({
  useCases = [],
  benefits = [],
  faqs = [],
  relatedResources = [],
  tag,
  allFeatures,
  markdowns,
  markdownCovers,
}) => {
  const selectedProductTitle =
    PRODUCTS.find((p) => getProductSlug(p.key) === tag)?.value ?? ''

  const featuresWithCoverImages = allFeatures.filter(
    ({ feature }) => !!feature.coverImage
  )

  return (
    <SubSection>
      <Box>
        {useCases.length > 0 && (
          <SubCategory>
            <SubCategoryTitle>
              <IconBook fill={theme.colors.blacks[30]} width={22} height={22} />
              <SubTitle>Common use cases</SubTitle>
            </SubCategoryTitle>
            <SubCategoryContent>
              <UseCasesContainer>
                {useCases.map((useCase, index) => (
                  <UseCase key={index}>
                    <UseCaseTitle>{useCase.name}</UseCaseTitle>
                    <UseCaseDescription>
                      {useCase.description}
                    </UseCaseDescription>
                  </UseCase>
                ))}
              </UseCasesContainer>
            </SubCategoryContent>
          </SubCategory>
        )}
        {benefits.length > 0 && (
          <SubCategory>
            <SubCategoryTitle>
              <IconEasy fill={theme.colors.blacks[30]} width={22} height={22} />
              <SubTitle>Key benefits</SubTitle>
            </SubCategoryTitle>
            <SubCategoryContent>
              <BenefitsContainer>
                {benefits.map((benefit, index) => (
                  <Benefit key={index}>
                    <BenefitTitle>{benefit.name}</BenefitTitle>
                    <BenefitDescription>
                      {benefit.description}
                    </BenefitDescription>
                  </Benefit>
                ))}
              </BenefitsContainer>
            </SubCategoryContent>
          </SubCategory>
        )}
        {faqs.length > 0 && (
          <SubCategory>
            <SubCategoryTitle>
              <IconQuestion
                fill={theme.colors.blacks[30]}
                width={22}
                height={22}
              />
              <SubTitle>Frequently asked</SubTitle>
            </SubCategoryTitle>
            <SubCategoryContent>
              <StyledFAQs
                items={faqs.map(({ question, _rawAnswer }) => ({
                  question,
                  sanityAnswer: _rawAnswer,
                }))}
              />
            </SubCategoryContent>
          </SubCategory>
        )}
        {relatedResources.length > 0 && (
          <SubCategory>
            <SubCategoryTitle>
              <IconBlog fill={theme.colors.blacks[30]} width={22} height={22} />
              <SubTitle>Related resources</SubTitle>
            </SubCategoryTitle>
            <SubCategoryContent>
              <ResourcesContainer>
                {relatedResources.map((resource, index) => (
                  <RelatedResource
                    resource={resource}
                    key={index}
                    markdowns={markdowns}
                    markdownCovers={markdownCovers}
                  />
                ))}
              </ResourcesContainer>
            </SubCategoryContent>
          </SubCategory>
        )}
      </Box>
      <TestimonialCarousel hideTabs />
      <PDFFeaturesContainer>
        <PDFFeaturesSubTitle>
          Other {selectedProductTitle} features{' '}
          {tag && (
            <StyledUniversalLink
              to={`/products/${kebabCase(tag)}/${FEATURE_SLUG}/`}
            >
              See all
            </StyledUniversalLink>
          )}
        </PDFFeaturesSubTitle>
        <Box>
          <FeaturesGrid featuresCount={featuresWithCoverImages.length}>
            {featuresWithCoverImages.map(({ id, feature }) => (
              <FeatureContainer key={id}>
                <FeatureSummary
                  feature={feature}
                  tag={
                    tag ||
                    getProductSlug(feature.primaryProduct || feature.product[0])
                  }
                />
              </FeatureContainer>
            ))}
          </FeaturesGrid>
          <CarouselWrapper featuresCount={featuresWithCoverImages.length}>
            <StyledCarousel
              withArrows={false}
              slidesToShow={4}
              responsive={[
                {
                  breakpoint: theme.paperMaxWidth,
                  settings: {
                    slidesToShow: 3,
                  },
                },
                {
                  breakpoint: pixels.giant,
                  settings: {
                    slidesToShow: 2,
                  },
                },
                {
                  breakpoint: pixels.desktop,
                  settings: {
                    slidesToShow: 1,
                  },
                },
              ]}
              featuresCount={featuresWithCoverImages.length}
            >
              {featuresWithCoverImages
                .filter(({ feature }) => !!feature.coverImage)
                .map(({ id, feature }) => (
                  <FeatureContainer key={id}>
                    <FeatureSummary
                      feature={feature}
                      tag={
                        tag ||
                        getProductSlug(
                          feature.primaryProduct || feature.product[0]
                        )
                      }
                    />
                  </FeatureContainer>
                ))}
            </StyledCarousel>
          </CarouselWrapper>
        </Box>
      </PDFFeaturesContainer>
    </SubSection>
  )
}

FeatureContentSubSection.propTypes = {
  useCases: PropTypes.any,
  benefits: PropTypes.any,
  faqs: PropTypes.any,
  relatedResources: PropTypes.any,
  tag: PropTypes.string,
  allFeatures: PropTypes.arrayOf(PropTypes.object),
  markdowns: PropTypes.arrayOf(PropTypes.object),
  markdownCovers: PropTypes.arrayOf(PropTypes.object),
}

export default FeatureContentSubSection
